import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import moment from 'moment';

import { useDataAuth } from '../../../hooks/useDataAuth';
import { useNotification } from '../../../hooks/useNotification';

import { getCompanies } from '../../../services/service.service';
import { create, update, fetchRegister } from '../../../services/store.service';

export const useStore = ({
    values,
    setAllInfo,
    handleDirectInputChange,
    formRef,
    productsFormRef,
    t
}) => {

    const [{ userSession }] = useDataAuth();
    const [sendNotification] = useNotification();

    const navigate = useNavigate();
    const location = useLocation();

    const [loading, setLoading] = useState(false);
    const [isView, setIsView] = useState(false);
    const [edit, setEdit] = useState(false);
    const [workFlow, setWorkFlow] = useState('');
    const [companies, setCompanies] = useState([]);
    const [openDialogCompany, setOpenDialogCompany] = useState(false);

    const fetchDataService = useCallback(async (id) => {
        try {
            const response = await fetchRegister( id, userSession.token );
            if (response.status === 200 && response.data?.length > 0) {
                const baseData = response.data[0];
                const parsedProducts = Array.isArray(JSON.parse(baseData.products))
                    ? JSON.parse(baseData.products).map((p) => ({
                        id_warehouse_register_product: p.id_warehouse_register_product,
                        id_warehouse_register: p.id_warehouse_register,
                        id_product_service: p.id_warehouse_product_service,
                        code: p.code_sku || '',
                        name: p.name || '',
                        unit_description: p.unit_description || '',
                        quantity: p.amount ?? 0,
                        expected_quantity: p.amount_expected ?? 0,
                        state: p.status ?? '',
                        observation: p.observation ?? '',
                        isCreated: false,
                        created: p.created,
                        created_by: p.created_by
                    }))
                : [];
                setAllInfo({...baseData, dateOfEntry: baseData.date_of_entry ? moment(baseData.date_of_entry).toDate() : null, products: parsedProducts });
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, [userSession.token, setAllInfo]);

    useEffect(() => {
        if (!location.state?.config) return;
        const { config } = location.state;
        const wf = config.data?.work_flow;
        setWorkFlow(wf);
        if (config.data?.id_warehouse_register) {
            setLoading(true);
            if (wf === 'FLOW_PROCESS-VIEW_REGISTER') {
                setIsView(true);
            } else {
                setEdit(true);
            }
            fetchDataService(config.data.id_warehouse_register);
        } else {
            const loadCompanies = async () => {
                try {
                    setLoading(true);
                    const response = await getCompanies('50', userSession.token);
                    setCompanies(response.data || []);
                    if (response.data?.length === 1) {
                        handleDirectInputChange('id_company', response.data[0].id_company);
                    } else {
                        setOpenDialogCompany(true);
                    }
                } catch (error) {
                    console.error(error);
                } finally {
                    setLoading(false);
                }
            };
            loadCompanies();
        }
    }, [location.state, userSession.token]);

    const handleSubmit = async () => {
        const isValidForm = formRef.current?.validForm();
        const isValidProductsForm = productsFormRef.current?.validForm();
        if (!isValidForm || !isValidProductsForm) {
            sendNotification({ msg: t('validation.requiredFieldsMissing'), variant: 'error' });
            return;
        }
        try {
            setLoading(true);
            const data = {
                ...values,
                type: 'IN',
                type_document: 'Order Reference'
            };
            if (edit) {
                await update(data, userSession.token);
            } else {
                await create(data, userSession.token);
            }
            sendNotification({ msg: t('messages.saveSuccess'), variant: 'success' });
            navigate(-1);
        } finally {
            setLoading(false);
        }
    };

    const handleConfirmAction = (dto) => {
            handleDirectInputChange('id_company', dto.id_company);
            setOpenDialogCompany(false);
        };

    const handleCancel = () => {
        setOpenDialogCompany(false);
        navigate(-1);
    };

    return {
        loading,
        isView,
        edit,
        companies,
        openDialogCompany,
        handleSubmit,
        handleCancel,
        handleConfirmAction
    };
};